/// <amd-module name="typeahead/core/logging/PerformanceMetrics" />

import {abTest} from "typeahead/core/plugins/ExecutionHelper";
import {Lob, TypeaheadUtils} from "typeahead/core/TypeaheadUtils";

export interface ITypeaheadRequestMetric {
    inputElementId: string;
    query: string;
    timestamp: number;
    triggerType: string;
}

export interface ITypeaheadResponseMetric {
    essRegion?: string;
    inputElementId: string;
    latency: number;
    query: string;
    suggestionCount: number;
    timestamp: number;
}

export interface ITypeaheadTriggerMetric {
    request: ITypeaheadRequestMetric;
    response?: ITypeaheadResponseMetric;
    responseShown: boolean;
}

export interface IAdditionalMetric {
    deviceType: string;
    guid: string;
    lob: string;
    locale: string;
    pageId: string;
    pointOfSale: string;
    serviceEndpointId: string;
    siteId: string;
    userAgent: string;
}

export interface IAggregatedPerformanceMetric {
    essRegion?: string;
    latency0?: number;
    latency1?: number;
    latency2?: number;
    latency3?: number;
    latency4?: number;
    latency5?: number;
    latency6?: number;
    latency7?: number;
    latency8?: number;
    latency9?: number;
    latencyLast?: number;
    latencyWorst?: number;
    latencyMedian?: number;
    totalTriggers: number;
    totalSuccess: number;
    totalOnePlusCharTypeaheadShown: number;
    onFocusTypeaheadShown: boolean;
    queryWorst?: string;
    queryLast: string;
    textInputElementId: string;
    lob: string;
    variants: string;
    name: string;
    essErrorCount?: number;
    countZeroResultsByException?: number;
    serviceEndpointId: string;
    siteId: string;
    pageId: string;
    deviceType: string;
    contentFlavor: string;
}

const ESS_ENDPOINT_ID = "ess";
const AKAMAI_ENDPOINT_ID = "akamai";

export class PerformanceMetrics {
    public static logServiceEndpoint(serviceEndpoint: string): void {
        if (!PerformanceMetrics.serviceEndpointId) {
            PerformanceMetrics.serviceEndpointId = (serviceEndpoint === "") ? AKAMAI_ENDPOINT_ID : ESS_ENDPOINT_ID;
        }
    }
    private static serviceEndpointId: string;
    private static getAdditionalMetric(): IAdditionalMetric {
        return {
            deviceType: TypeaheadUtils.getDeviceType(),
            guid: TypeaheadUtils.getGuid(),
            lob: Lob[TypeaheadUtils.getLob()],
            locale: TypeaheadUtils.getLocale(),
            pageId: TypeaheadUtils.getPageId(),
            pointOfSale: TypeaheadUtils.getPointOfSale(),
            serviceEndpointId: PerformanceMetrics.serviceEndpointId,
            siteId: TypeaheadUtils.getSiteId(),
            userAgent: navigator.userAgent,
        };
    }

    private entries: ITypeaheadTriggerMetric[] = [];
    private readonly MAX_TRIGGER_COUNT_FOR_AGGREGATED_LOGGING = 10;
    private readonly METRIC_NAME = "UtaPerformanceMetric";
    private activeEssTests: string;
    private essErrorCount: number = 0;
    private countZeroResultsByException: number = 0;

    public logRequest(triggerType: string, inputElementId: string, query: string): void {
        const request: ITypeaheadRequestMetric = {
            inputElementId,
            query,
            timestamp: Date.now(),
            triggerType,
        };
        this.entries.push({
            request,
            responseShown: false,
        });
    }

    public logResponse(inputElementId: string, query: string, suggestionCount: number, activeEssTests: string,
                       metadata: string): void {
        const entry: ITypeaheadTriggerMetric =
            this.getRequestMetricEntry(inputElementId, query);
        if (entry) {
            const responseTimestamp: number = Date.now();
            entry.response = {
                essRegion: metadata ? metadata.replace("essRegion=", "") : "",
                inputElementId,
                latency: (responseTimestamp - entry.request.timestamp),
                query,
                suggestionCount,
                timestamp: responseTimestamp,
            };
            if (!this.activeEssTests && activeEssTests) {
                this.activeEssTests = activeEssTests;
            }
        }
    }

    public logResponseShown(inputElementId: string, query: string): void {
        const entry: ITypeaheadTriggerMetric =
            this.getResponseMetricEntry(inputElementId, query);
        if (entry) {
            entry.responseShown = true;
        }
    }

    public logError(statusCode): void {
        if (TypeaheadUtils.ZERO_RESULTS_CAUSED_BY_EXCEPTION === statusCode) {
            this.countZeroResultsByException++;
        } else if (!TypeaheadUtils.isStatusCodeInRange(statusCode, 200, 299)) {
            this.essErrorCount++;
        }
    }

    public getAggregatedMetric(): IAggregatedPerformanceMetric {
        if (this.entries.length) {
            const metric: IAggregatedPerformanceMetric = {} as IAggregatedPerformanceMetric;

            const entryCount = this.MAX_TRIGGER_COUNT_FOR_AGGREGATED_LOGGING < this.entries.length ?
                this.MAX_TRIGGER_COUNT_FOR_AGGREGATED_LOGGING : this.entries.length;
            for (let i: number = 0; i < entryCount; i++) {
                const entry: ITypeaheadTriggerMetric = this.entries[i];
                if (entry.response) {
                    metric["latency" + i] = entry.response.latency;
                    metric.essRegion = (metric.essRegion || entry.response.essRegion);
                }
            }
            metric.latencyMedian = this.getMedianLatency();

            const worstLatencyMetric: ITypeaheadTriggerMetric = this.getWorstLatencyMetric();
            metric.queryWorst = worstLatencyMetric ? this.getWorstLatencyMetric().response.query : undefined;
            metric.latencyWorst = worstLatencyMetric ? this.getWorstLatencyMetric().response.latency : undefined;

            const last: ITypeaheadTriggerMetric = this.entries[this.entries.length - 1];
            metric.queryLast = last.request.query;
            metric.latencyLast = last.response ? last.response.latency : undefined;

            metric.totalTriggers = this.entries.length;
            metric.totalSuccess = this.getSuccessMetrics().length;
            metric.totalOnePlusCharTypeaheadShown =
              this.entries.filter((entry) => entry.request.query && entry.responseShown).length;
            metric.onFocusTypeaheadShown = this.entries[0].responseShown;
            metric.textInputElementId = this.entries[0].request.inputElementId;
            metric.lob = Lob[TypeaheadUtils.getLob()];
            metric.variants = [abTest.getActiveClientTests(), this.activeEssTests].filter(Boolean).join("|");
            metric.name = this.METRIC_NAME;
            if (this.essErrorCount > 0) {
                metric.essErrorCount = this.essErrorCount;
            }
            if (this.countZeroResultsByException > 0) {
                metric.countZeroResultsByException = this.countZeroResultsByException;
            }
            metric.serviceEndpointId = PerformanceMetrics.serviceEndpointId;
            metric.siteId = TypeaheadUtils.getSiteId();
            metric.pageId = TypeaheadUtils.getPageId();
            metric.deviceType = TypeaheadUtils.getDeviceType();
            metric.contentFlavor = "uTA";
            return metric;
        }
        return undefined;
    }

    public getAllEntries(): Array<ITypeaheadRequestMetric & IAdditionalMetric> |
        Array<ITypeaheadResponseMetric & IAdditionalMetric> {
        const logEntries = [];
        for (let i: number = 0; i < this.entries.length; i++) {
            logEntries.push($.extend(this.entries[i].request, PerformanceMetrics.getAdditionalMetric()));
            if (this.entries[i].response) {
                delete this.entries[i].response.essRegion;
                logEntries.push(
                    $.extend(this.entries[i].response, PerformanceMetrics.getAdditionalMetric()));
            }
        }
        return logEntries;
    }

    private getRequestMetricEntry(inputElementId: string, query: string) {
        for (let i: number = 0; i < this.entries.length; i++) {
            const entry: ITypeaheadTriggerMetric = this.entries[i];
            if (entry.request.inputElementId === inputElementId && entry.request.query === query && !entry.response ) {
                return entry;
            }
        }
        return undefined;
    }

    private getResponseMetricEntry(inputElementId: string, query: string) {
        for (let i: number = 0; i < this.entries.length; i++) {
            const entry: ITypeaheadTriggerMetric = this.entries[i];
            if (entry.request.inputElementId === inputElementId && entry.request.query === query && entry.response ) {
                return entry;
            }
        }
        return undefined;
    }

    private getWorstLatencyMetric(): ITypeaheadTriggerMetric {
        let worstLatencyMetric: ITypeaheadTriggerMetric;
        for (let i: number = 0; i < this.entries.length; i++) {
            const entry: ITypeaheadTriggerMetric = this.entries[i];
            if (entry.response) {
                if (!worstLatencyMetric || worstLatencyMetric.response.latency < entry.response.latency) {
                    worstLatencyMetric = entry;
                }
            }
        }
        return worstLatencyMetric;
    }

    private getMedianLatency(): number {
        let medianLatency: number;
        const successMetrics: ITypeaheadTriggerMetric[] = this.getSuccessMetrics();
        successMetrics.sort((a: ITypeaheadTriggerMetric, b: ITypeaheadTriggerMetric) => {
            return a.response.latency - b.response.latency;
        });
        if (successMetrics.length) {
            const mid = Math.floor(successMetrics.length / 2);
            medianLatency = successMetrics.length % 2 ? successMetrics[mid].response.latency :
                ((successMetrics[mid - 1].response.latency + successMetrics[mid].response.latency) / 2);
        }
        return medianLatency;
    }

    private getSuccessMetrics(): ITypeaheadTriggerMetric[] {
        return this.entries.filter((entry) => {
            return entry.response;
        });
    }
}
